// src/components/auth/Login.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { firebaseInfo } from '../../config/supabase';
import { authErrorMessage } from '../../utils/authErrorMessage';
import { useRedirectIfAuthenticated } from '../../hooks/useRedirectIfAuthenticated';
import LoginView from '../auth-v2/LoginView';

export default function Login() {
  const { login, loginWithGoogle, sendMagicLink, isMagicLinkUrl, completeMagicLinkSignIn, resetPassword } = useAuth();
  const navigate = useNavigate();

  // view: 'completing' (finishing a magic-link sign-in) | 'choice' (the
  // three entry points from Part 4) | 'email-entry' | 'email-sent' |
  // 'password' (the pre-existing fallback form, unchanged in behavior) |
  // 'password-reset' (P-AUTH-HOTFIX-2A: request a reset link) |
  // 'password-reset-sent' (neutral confirmation, see handleSendPasswordReset).
  const [view, setView] = useState('choice');
  const [email, setEmail] = useState('');
  const [passwordData, setPasswordData] = useState({ email: '', password: '' });
  const [resetEmail, setResetEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Separate, calmer channel from `error`: session-expiry is not a failure
  // the user caused, so Auth V2 shows it as a quiet notice rather than a
  // red error banner. Same sessionStorage key/trigger/timing as before.
  const [notice, setNotice] = useState('');

  // P-AUTH-HOTFIX-2B: recovers a Google sign-in that completed via a
  // full-page redirect (the mobile popup-blocked fallback) — that reloads
  // the app fresh on /login with the session already restored by
  // AuthContext, but nothing was navigating away from this screen. Held off
  // while a magic-link completion is in flight so that flow's own
  // navigate('/dashboard') (below) always wins.
  useRedirectIfAuthenticated(view === 'completing');

  // P0 fix: an involuntary session expiry (see config/supabase.js —
  // clearInvalidSession) lands the user here with currentUser cleared and no
  // explanation. Show the one message the mission asks for, then clear the
  // flag so a normal future visit to /login stays silent.
  useEffect(() => {
    try {
      if (window.sessionStorage.getItem('nsara_session_expired')) {
        setNotice('Votre session a expiré. Veuillez vous reconnecter.');
        window.sessionStorage.removeItem('nsara_session_expired');
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (isMagicLinkUrl()) {
      setView('completing');
      completeMagicLinkSignIn()
        .then(() => { if (!cancelled) navigate('/dashboard'); })
        .catch(() => {
          if (!cancelled) {
            setError("Ce lien de connexion n'est plus valide. Veuillez réessayer.");
            setView('choice');
          }
        });
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSendMagicLink = async (event) => {
    event.preventDefault();
    if (!email.trim()) { setError('Veuillez saisir votre adresse e-mail'); return; }
    setLoading(true);
    setError('');
    try {
      await sendMagicLink(email);
      setView('email-sent');
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    if (!passwordData.email || !passwordData.password) {
      setError('Veuillez remplir tous les champs');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await login(passwordData.email, passwordData.password);
      navigate('/dashboard');
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      await loginWithGoogle();
      navigate('/dashboard');
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const goTo = (nextView) => { setError(''); setNotice(''); setView(nextView); };

  // Prefills from whatever the person already typed in the password view,
  // so asking for a reset doesn't make them retype an e-mail they just gave.
  const goToPasswordReset = () => {
    setResetEmail(passwordData.email || '');
    setError('');
    setNotice('');
    setView('password-reset');
  };

  const onResetEmailChange = (event) => { setResetEmail(event.target.value); setError(''); };

  const handleSendPasswordReset = async (event) => {
    event.preventDefault();
    if (!resetEmail.trim()) { setError('Veuillez saisir votre adresse e-mail'); return; }
    setLoading(true);
    setError('');
    try {
      await resetPassword(resetEmail);
      setView('password-reset-sent');
    } catch (err) {
      // Whether the address has an NSARA account is never revealed: a
      // "no such user" error still lands on the same neutral confirmation
      // as a real send, so the two cases are indistinguishable to a visitor.
      const normalized = `${err?.code || ''} ${err?.message || ''}`.toUpperCase();
      if (normalized.includes('EMAIL_NOT_FOUND') || normalized.includes('USER_NOT_FOUND')) {
        setView('password-reset-sent');
      } else {
        setError(authErrorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const onPasswordFieldChange = (field) => (event) => {
    const { value } = event.target;
    setPasswordData((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  return (
    <LoginView
      view={view}
      email={email}
      onEmailChange={(event) => { setEmail(event.target.value); setError(''); }}
      passwordData={passwordData}
      onPasswordFieldChange={onPasswordFieldChange}
      resetEmail={resetEmail}
      onResetEmailChange={onResetEmailChange}
      loading={loading}
      error={error}
      notice={notice}
      googleEnabled={firebaseInfo.enabled}
      onSendMagicLink={handleSendMagicLink}
      onPasswordSubmit={handlePasswordSubmit}
      onGoogleLogin={handleGoogleLogin}
      onGoToPasswordReset={goToPasswordReset}
      onSendPasswordReset={handleSendPasswordReset}
      goTo={goTo}
    />
  );
}
